import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { IRiderEventHistory, IRiderLatestState, RiderTrackingStatus } from '@admin-panel/types';
import CrudApiClient from './crudapiclient';
import { axiosInstance } from './axios';

type LyskraftEnvelope<T> = {
  data?: T;
  success?: boolean;
  status?: {
    code?: number;
    key?: string;
    type?: string;
    message?: string;
  };
};

export type RiderLatestStateSearchRequest = {
  riderId?: number;
  deviceId?: string;
  trackingStatuses?: Array<RiderTrackingStatus>;
  lastPingBefore?: string;
  lastPingAfter?: string;
};

class RiderLatestStateApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/rider-latest-state');
  }

  async searchLatestStates(
    query: RiderLatestStateSearchRequest,
    page = 1,
    size = 50,
  ): Promise<Array<IRiderLatestState>> {
    const response = await axiosInstance.post(`${this.baseURL}/search?page=${page}&size=${size}`, query ?? {});
    return this.transformList(
      unwrapEnvelope<Array<IRiderLatestState>>(response.data, 'Could not load rider latest state') ?? [],
    );
  }

  async getByRiderId(riderId: number): Promise<IRiderLatestState | null> {
    const response = await axiosInstance.get(`${this.baseURL}/rider/${riderId}`);
    const state = unwrapEnvelope<IRiderLatestState | null>(
      response.data,
      'Could not load rider latest state',
    );
    return state ? this.transformNode(state) : null;
  }

  async getRiderEvents(riderId: number, limit = 50): Promise<Array<IRiderEventHistory>> {
    const safeLimit = Math.min(Math.max(limit, 1), 100);
    const response = await axiosInstance.get(`${this.baseURL}/rider/${riderId}/events?limit=${safeLimit}`);
    return unwrapEnvelope<Array<IRiderEventHistory>>(response.data, 'Could not load rider event history') ?? [];
  }
}

const unwrapEnvelope = <T>(envelope: LyskraftEnvelope<T>, fallbackMessage: string): T => {
  if (envelope?.success === false || envelope?.status?.type === 'ERROR') {
    const error = new Error(envelope?.status?.message || envelope?.status?.key || fallbackMessage) as Error & {
      statusCode?: number;
      statusKey?: string;
    };
    error.statusCode = envelope?.status?.code;
    error.statusKey = envelope?.status?.key;
    throw error;
  }
  return envelope?.data as T;
};

export default RiderLatestStateApiClient;
